import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { useGlobalState } from '../global/context/global-context';

export function PrivacyPolicyComponent() {
  const { applicationData } = useGlobalState();
  const entityName = applicationData?.entity?.nickName || 'Prefeitura Municipal';

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title="Política de Privacidade"
          subtitle="Como tratamos seus dados pessoais"
          breadcrumbs={[
            { label: 'Política de Privacidade' },
          ]}
        />

        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 4,
              pb: 2,
              borderBottom: '2px solid',
              borderColor: 'primary.main',
            }}
          >
            <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h2" sx={{ fontWeight: 600 }}>
              Política de Privacidade e Proteção de Dados
            </Typography>
          </Box>

          <Box sx={{ '& > *': { mb: 3 } }}>
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
                A {entityName} está comprometida com a proteção da privacidade e dos dados pessoais dos
                cidadãos que utilizam este portal. Esta Política de Privacidade descreve como coletamos,
                usamos, armazenamos e protegemos suas informações pessoais, em conformidade com a Lei Geral
                de Proteção de Dados (LGPD - Lei nº 13.709/2018).
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                1. Informações que Coletamos
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
                Podemos coletar as seguintes categorias de informações:
              </Typography>
              <Box component="ul" sx={{ pl: 4, color: 'text.secondary', '& li': { mb: 1 } }}>
                <li>
                  <strong>Informações de Identificação:</strong> Nome, CPF, RG, endereço, telefone e e-mail
                </li>
                <li>
                  <strong>Informações de Navegação:</strong> Endereço IP, tipo de navegador, páginas acessadas,
                  data e hora de acesso
                </li>
                <li>
                  <strong>Cookies e Tecnologias Similares:</strong> Informações coletadas automaticamente para
                  melhorar a experiência do usuário
                </li>
                <li>
                  <strong>Informações Fornecidas Voluntariamente:</strong> Dados fornecidos em formulários de
                  contato, solicitações de serviços ou manifestações
                </li>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                2. Como Usamos suas Informações
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
                Utilizamos suas informações pessoais para as seguintes finalidades:
              </Typography>
              <Box component="ul" sx={{ pl: 4, color: 'text.secondary', '& li': { mb: 1 } }}>
                <li>Fornecer e melhorar os serviços públicos municipais</li>
                <li>Processar solicitações, requerimentos e manifestações</li>
                <li>Responder a perguntas e fornecer suporte ao cidadão</li>
                <li>Cumprir obrigações legais e regulatórias</li>
                <li>Enviar comunicações sobre serviços e atualizações importantes</li>
                <li>Realizar análises estatísticas e melhorias no portal</li>
                <li>Garantir a segurança e integridade do portal</li>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                3. Base Legal para o Tratamento de Dados
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
                O tratamento de dados pessoais é fundamentado nas seguintes bases legais:
              </Typography>
              <Box component="ul" sx={{ pl: 4, color: 'text.secondary', '& li': { mb: 1 } }}>
                <li>Execução de políticas públicas previstas em leis e regulamentos</li>
                <li>Cumprimento de obrigação legal ou regulatória</li>
                <li>Exercício regular de direitos em processo judicial, administrativo ou arbitral</li>
                <li>Consentimento do titular dos dados, quando aplicável</li>
                <li>Atendimento aos interesses legítimos da Administração Pública</li>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                4. Compartilhamento de Informações
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
                Podemos compartilhar suas informações pessoais nas seguintes situações:
              </Typography>
              <Box component="ul" sx={{ pl: 4, color: 'text.secondary', '& li': { mb: 1 } }}>
                <li>Com outros órgãos públicos municipais, estaduais ou federais, quando necessário para
                  prestação de serviços públicos</li>
                <li>Com prestadores de serviços e fornecedores que auxiliam na operação do portal</li>
                <li>Quando exigido por lei ou ordem judicial</li>
                <li>Para proteger os direitos, propriedade ou segurança da Administração Pública</li>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mt: 2 }}>
                Não vendemos, alugamos ou compartilhamos suas informações pessoais com terceiros para fins
                comerciais.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                5. Cookies e Tecnologias de Rastreamento
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Utilizamos cookies e tecnologias similares para melhorar a experiência do usuário, analisar o
                tráfego do portal e personalizar conteúdo. Você pode configurar seu navegador para recusar
                cookies, mas isso pode afetar a funcionalidade do portal.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                6. Segurança das Informações
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Implementamos medidas técnicas e organizacionais apropriadas para proteger suas informações
                pessoais contra acesso não autorizado, alteração, divulgação ou destruição. No entanto, nenhum
                método de transmissão pela internet ou armazenamento eletrônico é 100% seguro.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                7. Retenção de Dados
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Mantemos suas informações pessoais apenas pelo tempo necessário para cumprir as finalidades
                para as quais foram coletadas, incluindo requisitos legais, contábeis ou de relatórios. Os
                prazos de retenção seguem a legislação aplicável e as políticas de gestão documental da
                Administração Pública.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                8. Seus Direitos sob a LGPD
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
                De acordo com a LGPD, você tem os seguintes direitos em relação aos seus dados pessoais:
              </Typography>
              <Box component="ul" sx={{ pl: 4, color: 'text.secondary', '& li': { mb: 1 } }}>
                <li>Confirmação da existência de tratamento de dados</li>
                <li>Acesso aos seus dados pessoais</li>
                <li>Correção de dados incompletos, inexatos ou desatualizados</li>
                <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos</li>
                <li>Portabilidade dos dados a outro fornecedor de serviço</li>
                <li>Eliminação dos dados tratados com base no consentimento</li>
                <li>Informação sobre compartilhamento de dados</li>
                <li>Revogação do consentimento</li>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mt: 2 }}>
                Para exercer seus direitos, entre em contato através dos canais oficiais disponíveis no portal.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                9. Dados de Menores de Idade
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Este portal não coleta intencionalmente informações pessoais de menores de 18 anos sem o
                consentimento dos pais ou responsáveis legais. Se tomarmos conhecimento de que coletamos
                dados de menores sem autorização, tomaremos medidas para excluir essas informações.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                10. Alterações nesta Política
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Reservamo-nos o direito de atualizar esta Política de Privacidade periodicamente para refletir
                mudanças em nossas práticas ou requisitos legais. Recomendamos que você revise esta página
                regularmente para se manter informado sobre como protegemos suas informações.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                11. Encarregado de Dados (DPO)
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                O Encarregado de Proteção de Dados (DPO) da {entityName} é responsável por atender às
                solicitações dos titulares de dados e interagir com a Autoridade Nacional de Proteção de
                Dados (ANPD). Para questões relacionadas à proteção de dados, entre em contato através dos
                canais oficiais disponíveis no portal.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                12. Contato
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Se você tiver dúvidas sobre esta Política de Privacidade ou sobre o tratamento de seus dados
                pessoais, entre em contato conosco através dos canais oficiais da {entityName} disponíveis
                neste portal.
              </Typography>
            </Box>

            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Última atualização: {new Date().toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </PageContainer>
  );
}
